import AgoraRTC from 'agora-rtc-sdk-ng';

// Device management service
export class DeviceService {
  constructor() {
    this.devices = { microphones: [], speakers: [], cameras: [] };
    this.selectedMic = '';
    this.selectedSpeaker = '';
    this.selectedCamera = '';
    this.remoteAudioTracks = []; // Store remote audio tracks for speaker switching
    this.stethoscopeDevice = null; // Store detected stethoscope device
  }

  // Fetch available devices
  async fetchDevices() {
    try {
      const devices = await AgoraRTC.getDevices();
      this.devices.microphones = devices.filter(device => device.kind === 'audioinput');
      this.devices.speakers = devices.filter(device => device.kind === 'audiooutput');
      this.devices.cameras = devices.filter(device => device.kind === 'videoinput');

      console.log('🎛️ Raw devices fetched:');
      console.log('  Microphones:', this.devices.microphones.length);
      console.log('  Speakers:', this.devices.speakers.length);
      console.log('  Cameras:', this.devices.cameras.length);

      // Detect stethoscope devices
      // Stethoscope detection disabled for Mentorzaa

      // Load saved device preferences from localStorage
      const savedDevices = localStorage.getItem('selectedDevices');
      if (savedDevices) {
        const parsed = JSON.parse(savedDevices);
        console.log('💾 Loaded saved preferences:', parsed);
        
        if (parsed.microphone && this.devices.microphones.find(d => d.deviceId === parsed.microphone)) {
          this.selectedMic = parsed.microphone;
          console.log('✅ Restored saved microphone:', this.selectedMic);
        }
        if (parsed.speaker && this.devices.speakers.find(d => d.deviceId === parsed.speaker)) {
          this.selectedSpeaker = parsed.speaker;
          console.log('✅ Restored saved speaker:', this.selectedSpeaker);
        }
        if (parsed.camera && this.devices.cameras.find(d => d.deviceId === parsed.camera)) {
          this.selectedCamera = parsed.camera;
          console.log('✅ Restored saved camera:', this.selectedCamera);
        }
      }

      // Set defaults if not already set
      if (!this.selectedMic && this.devices.microphones.length > 0) {
        this.selectedMic = this.devices.microphones[0].deviceId;
        console.log('🎤 Set default microphone:', this.selectedMic);
      }
      if (!this.selectedSpeaker && this.devices.speakers.length > 0) {
        this.selectedSpeaker = this.devices.speakers[0].deviceId;
        console.log('🔊 Set default speaker:', this.selectedSpeaker);
      }
      if (!this.selectedCamera && this.devices.cameras.length > 0) {
        this.selectedCamera = this.devices.cameras[0].deviceId;
        console.log('📹 Set default camera:', this.selectedCamera);
      }

      console.log('🎛️ Available devices:', this.devices);
      console.log('🎛️ Selected devices:', this.getSelectedDevices());
      return this.devices;
    } catch (err) {
      console.error('❌ Failed to fetch devices:', err);
      throw err;
    }
  }

  // Switch microphone
  async switchMicrophone(deviceId, audioTrack) {
    try {
      console.log('🎤 ===== SWITCHING MICROPHONE =====');
      console.log('🎤 Target device ID:', deviceId);
      console.log('🎤 Audio track exists:', !!audioTrack);
      
      if (!audioTrack) {
        console.error('❌ No audio track provided');
        return false;
      }

      if (!deviceId) {
        console.error('❌ No device ID provided');
        return false;
      }

      // Check if device exists
      const deviceExists = this.devices.microphones.find(d => d.deviceId === deviceId);
      if (!deviceExists) {
        console.error('❌ Microphone device not found:', deviceId);
        console.log('Available microphones:', this.devices.microphones.map(d => ({
          id: d.deviceId,
          label: d.label
        })));
        return false;
      }

      console.log('✅ Device found:', deviceExists.label);
      console.log('🔄 Calling audioTrack.setDevice()...');

      await audioTrack.setDevice(deviceId);
      
      this.selectedMic = deviceId;
      console.log('✅ Successfully switched to microphone:', deviceId);
      console.log('✅ Device label:', deviceExists.label);

      // Update local storage to persist device selection
      this.saveDevicePreference('microphone', deviceId);

      return true;
    } catch (err) {
      console.error('❌ Failed to switch microphone:', err);
      console.error('❌ Error details:', {
        message: err.message,
        code: err.code,
        name: err.name
      });
      return false;
    }
  }

  // Switch speaker - applies to remote audio tracks
  async switchSpeaker(deviceId) {
    try {
      console.log('🔊 ===== SWITCHING SPEAKER =====');
      console.log('🔊 Target device ID:', deviceId);
      console.log('🔊 Remote audio tracks count:', this.remoteAudioTracks.length);

      if (!deviceId) {
        console.error('❌ No device ID provided');
        return false;
      }

      // Check if device exists
      const deviceExists = this.devices.speakers.find(d => d.deviceId === deviceId);
      if (!deviceExists) {
        console.error('❌ Speaker device not found:', deviceId);
        console.log('Available speakers:', this.devices.speakers.map(d => ({
          id: d.deviceId,
          label: d.label
        })));
        return false;
      }

      console.log('✅ Device found:', deviceExists.label);

      // Apply to all remote audio tracks
      if (this.remoteAudioTracks.length > 0) {
        console.log('🔄 Applying to', this.remoteAudioTracks.length, 'remote track(s)...');
        let successCount = 0;

        for (const track of this.remoteAudioTracks) {
          try {
            if (track && !track.isDestroyed) {
              await track.setPlaybackDevice(deviceId);
              successCount++;
              console.log('✅ Applied to track', successCount);
            } else {
              console.warn('⚠️ Skipping destroyed track');
            }
          } catch (trackErr) {
            console.warn('⚠️ Failed to set playback device for one track:', trackErr);
          }
        }

        console.log(`✅ Successfully switched speaker for ${successCount}/${this.remoteAudioTracks.length} track(s)`);
      } else {
        console.log('ℹ️ No remote audio tracks yet, speaker preference saved for when tracks arrive');
      }

      this.selectedSpeaker = deviceId;
      console.log('✅ Speaker selection saved:', deviceId);

      // Update local storage to persist device selection
      this.saveDevicePreference('speaker', deviceId);

      return true;
    } catch (err) {
      console.error('❌ Failed to switch speaker:', err);
      console.error('❌ Error details:', {
        message: err.message,
        code: err.code,
        name: err.name
      });
      return false;
    }
  }

  // Switch camera
  async switchCamera(deviceId, videoTrack) {
    try {
      console.log('📹 ===== SWITCHING CAMERA =====');
      console.log('📹 Target device ID:', deviceId);
      console.log('📹 Video track exists:', !!videoTrack);

      if (!videoTrack) {
        console.error('❌ No video track provided');
        return false;
      }

      if (!deviceId) {
        console.error('❌ No device ID provided');
        return false;
      }

      // Check if device exists
      const deviceExists = this.devices.cameras.find(d => d.deviceId === deviceId);
      if (!deviceExists) {
        console.error('❌ Camera device not found:', deviceId);
        console.log('Available cameras:', this.devices.cameras.map(d => ({
          id: d.deviceId,
          label: d.label
        })));
        return false;
      }

      console.log('✅ Device found:', deviceExists.label);
      console.log('🔄 Calling videoTrack.setDevice()...');

      await videoTrack.setDevice(deviceId);
      
      this.selectedCamera = deviceId;
      console.log('✅ Successfully switched to camera:', deviceId);
      console.log('✅ Device label:', deviceExists.label);

      // Update local storage to persist device selection
      this.saveDevicePreference('camera', deviceId);

      return true;
    } catch (err) {
      console.error('❌ Failed to switch camera:', err);
      console.error('❌ Error details:', {
        message: err.message,
        code: err.code,
        name: err.name
      });
      return false;
    }
  }

  // Add remote audio track for speaker switching
  addRemoteAudioTrack(track) {
    if (!track) {
      console.warn('⚠️ Attempted to add null/undefined remote audio track');
      return;
    }

    if (this.remoteAudioTracks.includes(track)) {
      console.log('ℹ️ Remote audio track already registered');
      return;
    }

    this.remoteAudioTracks.push(track);
    console.log('📢 ===== REMOTE AUDIO TRACK ADDED =====');
    console.log('📢 Total remote audio tracks:', this.remoteAudioTracks.length);

    // Apply current speaker selection to new track
    if (this.selectedSpeaker) {
      console.log('🔊 Applying saved speaker preference to new track:', this.selectedSpeaker);
      track.setPlaybackDevice(this.selectedSpeaker)
        .then(() => {
          console.log('✅ Speaker preference applied to new remote track');
        })
        .catch(err => {
          console.warn('⚠️ Failed to set speaker for new remote track:', err);
        });
    }
  }

  // Force refresh speaker for all tracks (useful for testing)
  async refreshSpeakerForAllTracks() {
    if (!this.selectedSpeaker || this.remoteAudioTracks.length === 0) {
      console.log('ℹ️ No speaker selected or no tracks to refresh');
      return;
    }

    console.log('🔄 Refreshing speaker for all tracks...');
    let successCount = 0;

    for (const track of this.remoteAudioTracks) {
      try {
        if (track && !track.isDestroyed) {
          await track.setPlaybackDevice(this.selectedSpeaker);
          successCount++;
        }
      } catch (err) {
        console.warn('⚠️ Failed to refresh speaker for track:', err);
      }
    }

    console.log(`✅ Refreshed speaker for ${successCount} tracks`);
  }

  // Remove remote audio track
  removeRemoteAudioTrack(track) {
    const index = this.remoteAudioTracks.indexOf(track);
    if (index > -1) {
      this.remoteAudioTracks.splice(index, 1);
      console.log('📢 ===== REMOTE AUDIO TRACK REMOVED =====');
      console.log('📢 Remaining remote audio tracks:', this.remoteAudioTracks.length);
    } else {
      console.warn('⚠️ Attempted to remove remote audio track that was not registered');
    }
  }

  // Helper to save device preference
  saveDevicePreference(deviceType, deviceId) {
    try {
      const savedDevices = localStorage.getItem('selectedDevices');
      const devices = savedDevices ? JSON.parse(savedDevices) : {};
      devices[deviceType] = deviceId;
      localStorage.setItem('selectedDevices', JSON.stringify(devices));
      console.log('💾 Saved device preference:', deviceType, '=', deviceId);
    } catch (err) {
      console.warn('⚠️ Failed to save device preference:', err);
    }
  }

  // Get device options for UI
  getDeviceOptions() {
    return {
      microphones: this.devices.microphones.map(device => ({
        value: device.deviceId,
        label: device.label || `Microphone ${device.deviceId.substring(0, 8)}`
      })),
      speakers: this.devices.speakers.map(device => ({
        value: device.deviceId,
        label: device.label || `Speaker ${device.deviceId.substring(0, 8)}`
      })),
      cameras: this.devices.cameras.map(device => ({
        value: device.deviceId,
        label: device.label || `Camera ${device.deviceId.substring(0, 8)}`
      }))
    };
  }

  // Get selected devices
  getSelectedDevices() {
    return {
      microphone: this.selectedMic,
      speaker: this.selectedSpeaker,
      camera: this.selectedCamera
    };
  }

  // Reset devices
  reset() {
    console.log('🔄 Resetting device service...');
    this.devices = { microphones: [], speakers: [], cameras: [] };
    this.selectedMic = '';
    this.selectedSpeaker = '';
    this.selectedCamera = '';
    this.remoteAudioTracks = [];
    console.log('✅ Device service reset complete');
  }

  // Clear saved device preferences
  clearSavedDevices() {
    localStorage.removeItem('selectedDevices');
    console.log('🗑️ Cleared saved device preferences');
  }

  // Detect stethoscope devices from available microphones
  detectStethoscopeDevices() {
    console.log('🩺 ===== DETECTING STETHOSCOPE DEVICES =====');

    // Common stethoscope device name patterns - more specific to avoid false positives
    const stethoscopePatterns = [
      /stethoscope/i,
      /steth/i,
      /electronic.*steth/i,
      /digital.*steth/i,
      /cardiology/i,
      /auscultation/i,
      /littmann/i, // Common stethoscope brand
      /3m.*steth/i,
      /adc.*steth/i,
      /american.*diagnostic/i,
      /medical.*steth/i,
      /clinician.*steth/i,
      /physician.*steth/i,
      /usb.*steth/i,
      /bluetooth.*steth/i,
      /wireless.*steth/i
    ];

    // Exclude common generic device names that are NOT stethoscopes
    const excludePatterns = [
      /high.*definition.*audio/i,
      /default.*microphone/i,
      /built.*in.*microphone/i,
      /internal.*microphone/i,
      /microphone.*array/i,
      /webcam.*microphone/i,
      /headset.*microphone/i,
      /earbuds.*microphone/i,
      /speakers.*microphone/i
    ];

    let detectedStethoscope = null;

    // Check each microphone for stethoscope patterns
    for (const mic of this.devices.microphones) {
      const deviceLabel = mic.label || '';
      const deviceId = mic.deviceId || '';

      console.log(`🔍 Checking device: "${deviceLabel}" (ID: ${deviceId.substring(0, 8)}...)`);

      // Check if device should be excluded (generic devices)
      const shouldExclude = excludePatterns.some(pattern =>
        pattern.test(deviceLabel) || pattern.test(deviceId)
      );

      if (shouldExclude) {
        console.log(`🚫 Excluding generic device: "${deviceLabel}"`);
        continue; // Skip this device
      }

      // Check if device label matches any stethoscope pattern
      const isStethoscope = stethoscopePatterns.some(pattern =>
        pattern.test(deviceLabel) || pattern.test(deviceId)
      );

      if (isStethoscope) {
        detectedStethoscope = mic;
        console.log('🩺 ✅ STETHOSCOPE DETECTED!');
        console.log('🩺 Device Label:', deviceLabel);
        console.log('🩺 Device ID:', deviceId);
        break; // Take the first match
      }
    }

    this.stethoscopeDevice = detectedStethoscope;

    if (detectedStethoscope) {
      console.log('🩺 ===== STETHOSCOPE FOUND =====');
      console.log('🩺 Label:', detectedStethoscope.label);
      console.log('🩺 ID:', detectedStethoscope.deviceId);

      // Automatically select stethoscope as microphone if available
      if (!this.selectedMic || this.selectedMic !== detectedStethoscope.deviceId) {
        console.log('🩺 Auto-selecting stethoscope as microphone...');
        this.selectedMic = detectedStethoscope.deviceId;
        this.saveDevicePreference('microphone', detectedStethoscope.deviceId);
      }
    } else {
      console.log('🩺 No stethoscope devices detected');
      console.log('🩺 Available microphones:', this.devices.microphones.map(m => m.label || 'Unknown'));

      // If no stethoscope found, check if we should prompt user to connect one
      // or if they want to use a different device as stethoscope
      if (this.devices.microphones.length > 0) {
        console.log('💡 Tip: If you have a stethoscope connected, try refreshing devices or check device name patterns');
      }
    }

    return detectedStethoscope;
  }

  // Get stethoscope device info
  getStethoscopeInfo() {
    if (!this.stethoscopeDevice) {
      return null;
    }

    return {
      deviceId: this.stethoscopeDevice.deviceId,
      label: this.stethoscopeDevice.label || 'Stethoscope',
      isSelected: this.selectedMic === this.stethoscopeDevice.deviceId,
      isAvailable: true
    };
  }

  // Check if stethoscope is currently selected
  isStethoscopeSelected() {
    return this.stethoscopeDevice && this.selectedMic === this.stethoscopeDevice.deviceId;
  }
}